import { Button } from '@mantine/core';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './TestAccountsPanel.module.css';

/** Paneldeki bir hesap (`GET /api/demo/test-accounts` satırı). */
export interface TestAccountItem {
  email: string;
  fullName: string;
  role: string;
}

/** Hesap grubu: bir gemi ya da ofis. `usable: false` yalnız bilgi içindir ("Use" yok). */
export interface TestAccountGroup {
  key: string;
  title: string;
  /** Bilgi grubunun nerede girildiği (ör. "sign in at the office web app"). */
  note?: string;
  usable: boolean;
  accounts: readonly TestAccountItem[];
}

/**
 * Bağlı test ortamı hesapları (ADR 0092; sahip kararı 2026-10-20): giriş ekranında her rütbe ve
 * ofis rolünün hesabı ve ortak test parolası açıkça; "Use" formu doldurur. Parola sunucu
 * ayarından gelir, repoda yoktur; ayar kapalıysa panel hiç çizilmez. Gemi uygulaması (o geminin
 * mürettebatı, ofis bilgi için) ve web ofis (ofis hesapları, gemiler bilgi için) ortak kullanır.
 */
export function TestAccountsPanel({
  company,
  password,
  groups,
  onUse,
  footer,
}: {
  company: string;
  password: string;
  groups: readonly TestAccountGroup[];
  onUse: (email: string, password: string) => void;
  /** Kartın altına ek satır (ör. gemide "Exit demo"). */
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <section className={classes.panel} aria-labelledby={`${id}-title`} data-test-accounts>
      <div className={classes.head}>
        <span className={classes.badge} aria-hidden>
          {t('testAccounts.badge')}
        </span>
        <div className={classes.titles}>
          <h2 className={classes.title} id={`${id}-title`}>
            {t('testAccounts.title')}
          </h2>
          <p className={classes.subtitle}>{t('testAccounts.subtitle', { company })}</p>
        </div>
      </div>
      <div className={classes.passwordRow}>
        <span>{t('testAccounts.passwordLabel')}</span>
        <code className={classes.password}>{password}</code>
      </div>
      {/* Uzun liste kendi içinde kayar; içindeki "Use" düğmeleriyle klavyeden erişilir */}
      <div className={classes.list}>
        {groups.map((group) => (
          <div key={group.key} className={classes.group}>
            <div className={classes.groupHead}>
              <span className={classes.groupTitle} id={`${id}-${group.key}`}>
                {group.title}
              </span>
              <span className={classes.muted}>
                {t('testAccounts.count', { count: group.accounts.length })}
                {group.note ? ` · ${group.note}` : ''}
              </span>
            </div>
            <ul className={classes.accounts} aria-labelledby={`${id}-${group.key}`}>
              {group.accounts.map((account) => (
                <li key={account.email} className={classes.account}>
                  <span className={classes.accountText}>
                    <span className={classes.line}>
                      <strong>{account.role}</strong>
                      {' · '}
                      {account.fullName}
                    </span>
                    <span className={`${classes.line} ${classes.muted}`}>{account.email}</span>
                  </span>
                  {group.usable ? (
                    <Button
                      size="compact-xs"
                      variant="white"
                      aria-label={t('testAccounts.useAccount', {
                        name: account.fullName,
                        role: account.role,
                      })}
                      onClick={() => onUse(account.email, password)}
                    >
                      {t('testAccounts.use')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className={classes.hint}>
        {t('testAccounts.hint')}
        {footer ? <> {footer}</> : null}
      </div>
    </section>
  );
}
